import {customRef, nextTick, watch, ref, hasInjectionContext} from 'vue'
import {useRouter, useRoute} from 'vue-router'

// This is used to associate each router instance with its query parameters.
const _queue = new WeakMap()

export function useRouteQuery(name, defaultValue, options = {}) {
	const {mode = 'replace', transform = value => value} = options

	const route = hasInjectionContext() ? useRoute() : undefined
	const router = hasInjectionContext() ? useRouter() : undefined

	// checks if the current router instance already has an entry in _queue;
	// if not, it creates a new Map for this router, that will store the query parameters for this specific router instance
	if (router && !_queue.has(router)) {
		_queue.set(router, new Map())
	}

	const _queriesQueue = _queue.get(router)
	let query = ref(route?.query[name])

	let _trigger

	const proxy = customRef((track, trigger) => {
		_trigger = trigger

		return {
			get() {
				track()
				return transform(query.value !== undefined ? query.value : defaultValue)
			},
			set(v) {
				if (query.value === v) return
				if (!_queriesQueue) return

				query.value = v
				_queriesQueue.set(name, v)
				trigger()

				// nextTick is used to defer the update of the URL until after the current render cycle;
				// this ensures that all changes are aggregated and applied at once.
				nextTick(() => {
					if (_queriesQueue.size === 0) return

					const newQueries = Object.fromEntries(_queriesQueue.entries())
					_queriesQueue.clear()

					const {params, hash} = route

					router[mode]({
						params,
						query: {...route.query, ...newQueries},
						hash,
					})
				})
			},
		}
	})

	// reactively monitor changes to the query parameter in the URL
	watch(
		() => route?.query[name],
		v => {
			query.value = v
			_trigger()
		},
		// Run the watcher synchronously when the observed value changes.
		{flush: 'sync'}
	)

	return proxy
}
